import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Select, Textarea } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { useCompanyOptions } from '@/features/crm/api';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError } from '@/lib/api';
import { useSaveContract, useSlaPolicies } from './api';
import { CONTRACT_STATUS_LABELS, CONTRACT_TYPE_LABELS } from './labels';
import type { Contract } from './types';

const FREQUENCIES = [
  ['ANNUAL', 'Annually'],
  ['HALF_YEARLY', 'Every six months'],
  ['QUARTERLY', 'Quarterly'],
  ['MONTHLY', 'Monthly'],
  ['ONE_TIME', 'One payment'],
] as const;

export function ContractDialog({
  contract,
  onClose,
  onSaved,
}: {
  contract?: Contract;
  onClose: () => void;
  onSaved?: (contract: Contract) => void;
}) {
  const save = useSaveContract();
  const { data: companies = [] } = useCompanyOptions();
  const { data: users = [] } = useUserOptions();
  const { data: policies = [] } = useSlaPolicies();

  const [form, setForm] = useState({
    name: contract?.name ?? '',
    companyId: contract?.companyId ?? '',
    contractType: contract?.contractType ?? 'AMC',
    startDate: contract?.startDate?.slice(0, 10) ?? '',
    endDate: contract?.endDate?.slice(0, 10) ?? '',
    value: contract?.value ?? '',
    billingFrequency: contract?.billingFrequency ?? 'ANNUAL',
    paymentTermsDays: contract?.paymentTermsDays ?? 30,
    slaPolicyId: contract?.slaPolicyId ?? '',
    coverageIncluded: contract?.coverageIncluded ?? '',
    coverageExcluded: contract?.coverageExcluded ?? '',
    autoRenew: contract?.autoRenew ?? false,
    renewalNoticeDays: contract?.renewalNoticeDays ?? 60,
    escalationPercent: contract?.escalationPercent ?? 0,
    ownerId: contract?.ownerId ?? '',
    status: contract?.status ?? 'DRAFT',
    freeVisits: contract?.freeVisits ?? 0,
    visitCharge: contract?.visitCharge ?? '',
    freeUntil: contract?.freeUntil?.slice(0, 10) ?? '',
    freeMonths: 0,
    prepaidHoursPerPeriod: 0,
    carryForwardPolicy: 'NONE',
    carryForwardCapHours: 0,
  });
  const [error, setError] = useState<string | null>(null);

  const set = (patch: Partial<typeof form>) => setForm({ ...form, ...patch });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{contract ? `Edit ${contract.number}` : 'New contract'}</DialogTitle>
          <DialogDescription>
            The coverage text appears on every ticket for this client, so an agent knows what is
            included before they promise anything.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input
                value={form.name}
                placeholder="Annual maintenance — production"
                onChange={(event) => set({ name: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Client</Label>
              <Select
                value={form.companyId}
                onChange={(event) => set({ companyId: event.target.value })}
              >
                <option value="">Choose a client</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select
                value={form.contractType}
                onChange={(event) => set({ contractType: event.target.value as never })}
              >
                {Object.entries(CONTRACT_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Starts</Label>
              <Input
                type="date"
                value={form.startDate}
                onChange={(event) => set({ startDate: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Ends</Label>
              <Input
                type="date"
                value={form.endDate}
                onChange={(event) => set({ endDate: event.target.value })}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Contract value</Label>
              <Input
                inputMode="decimal"
                placeholder="0.00"
                value={form.value}
                onChange={(event) => set({ value: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Billed</Label>
              <Select
                value={form.billingFrequency}
                onChange={(event) => set({ billingFrequency: event.target.value })}
              >
                {FREQUENCIES.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>SLA policy</Label>
              <Select
                value={form.slaPolicyId}
                onChange={(event) => set({ slaPolicyId: event.target.value })}
              >
                <option value="">Use the default rules</option>
                {policies.map((policy) => (
                  <option key={policy.id} value={policy.id}>
                    {policy.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>What is covered</Label>
            <Textarea
              rows={2}
              placeholder="Bug fixes, minor enhancements, monitoring, backups…"
              value={form.coverageIncluded}
              onChange={(event) => set({ coverageIncluded: event.target.value })}
            />
          </div>

          <div className="space-y-1.5">
            <Label>What is not covered</Label>
            <Textarea
              rows={2}
              placeholder="New modules, third-party costs, hardware…"
              value={form.coverageExcluded}
              onChange={(event) => set({ coverageExcluded: event.target.value })}
            />
          </div>

          <div className="space-y-3 rounded-lg border p-3">
            <div className="text-sm font-medium">Site visits</div>
            <p className="text-xs text-muted-foreground">
              How many visits are included, and what one beyond them costs. Every visit booked
              works out for itself which side of the line it falls on.
            </p>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Free visits in the term</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.freeVisits}
                  onChange={(event) => set({ freeVisits: Number(event.target.value) || 0 })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Charge per extra visit</Label>
                <Input
                  inputMode="decimal"
                  placeholder="250.00"
                  value={form.visitCharge}
                  onChange={(event) => set({ visitCharge: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">
                  {form.freeUntil ? 'Free until' : 'Free for (months)'}
                </Label>
                {form.freeUntil ? (
                  <Input
                    type="date"
                    value={form.freeUntil}
                    onChange={(event) => set({ freeUntil: event.target.value })}
                  />
                ) : (
                  <Input
                    type="number"
                    min={0}
                    max={120}
                    value={form.freeMonths}
                    onChange={(event) => set({ freeMonths: Number(event.target.value) || 0 })}
                  />
                )}
                <p className="text-[11px] text-muted-foreground">
                  Everything is free until then — charges start after.
                </p>
              </div>
            </div>
          </div>

          {!contract && (
            <div className="space-y-3 rounded-lg border p-3">
              <div className="text-sm font-medium">Prepaid support hours</div>
              <p className="text-xs text-muted-foreground">
                An hour bank is created for every billing period of the term. Time logged against
                this client&rsquo;s tickets draws on it, and is not billed again.
              </p>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Hours per period</Label>
                  <Input
                    type="number"
                    min={0}
                    value={form.prepaidHoursPerPeriod}
                    onChange={(event) =>
                      set({ prepaidHoursPerPeriod: Number(event.target.value) })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Unused hours</Label>
                  <Select
                    value={form.carryForwardPolicy}
                    onChange={(event) => set({ carryForwardPolicy: event.target.value })}
                  >
                    <option value="NONE">Expire at period end</option>
                    <option value="UNLIMITED">Carry forward in full</option>
                    <option value="CAPPED">Carry forward up to a cap</option>
                  </Select>
                </div>
                {form.carryForwardPolicy === 'CAPPED' && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Cap (hours)</Label>
                    <Input
                      type="number"
                      min={0}
                      value={form.carryForwardCapHours}
                      onChange={(event) =>
                        set({ carryForwardCapHours: Number(event.target.value) })
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Notice period (days)</Label>
              <Input
                type="number"
                min={0}
                value={form.renewalNoticeDays}
                onChange={(event) => set({ renewalNoticeDays: Number(event.target.value) })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Renewal uplift (%)</Label>
              <Input
                type="number"
                min={0}
                step={0.5}
                value={form.escalationPercent}
                onChange={(event) => set({ escalationPercent: Number(event.target.value) })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Owner</Label>
              <Select value={form.ownerId} onChange={(event) => set({ ownerId: event.target.value })}>
                <option value="">You</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                checked={form.autoRenew}
                onCheckedChange={(value) => set({ autoRenew: value === true })}
              />
              Renews automatically
            </label>
            <div className="ml-auto flex items-center gap-2">
              <Label className="text-xs">Status</Label>
              <Select
                className="h-8 w-auto text-sm"
                value={form.status}
                onChange={(event) => set({ status: event.target.value as never })}
              >
                {Object.entries(CONTRACT_STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={
              save.isPending ||
              form.name.trim().length < 2 ||
              !form.companyId ||
              !form.startDate ||
              !form.endDate
            }
            onClick={async () => {
              setError(null);
              try {
                const saved = await save.mutateAsync({
                  id: contract?.id,
                  name: form.name,
                  companyId: form.companyId,
                  contractType: form.contractType,
                  startDate: form.startDate,
                  endDate: form.endDate,
                  value: form.value || undefined,
                  billingFrequency: form.billingFrequency,
                  paymentTermsDays: form.paymentTermsDays,
                  slaPolicyId: form.slaPolicyId || undefined,
                  coverageIncluded: form.coverageIncluded || undefined,
                  coverageExcluded: form.coverageExcluded || undefined,
                  autoRenew: form.autoRenew,
                  renewalNoticeDays: form.renewalNoticeDays,
                  escalationPercent: form.escalationPercent || undefined,
                  ownerId: form.ownerId || undefined,
                  status: form.status,
                  freeVisits: form.freeVisits || undefined,
                  visitCharge: form.visitCharge || undefined,
                  // Either an explicit date or a number of months from the start.
                  freeUntil: form.freeUntil || undefined,
                  freeMonths: form.freeUntil ? undefined : form.freeMonths || undefined,
                  ...(contract
                    ? {}
                    : {
                        prepaidHoursPerPeriod: form.prepaidHoursPerPeriod || undefined,
                        carryForwardPolicy: form.carryForwardPolicy,
                        carryForwardCapHours: form.carryForwardCapHours || undefined,
                      }),
                });
                onSaved?.(saved);
                onClose();
              } catch (caught) {
                setError(
                  caught instanceof ApiRequestError ? caught.message : 'Could not save the contract.',
                );
              }
            }}
          >
            {save.isPending ? 'Saving…' : contract ? 'Save changes' : 'Create contract'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
